// YouTube URL 패턴 감지 및 썸네일 추출 유틸리티

/**
 * YouTube 비디오 ID 추출 함수
 */
export function extractYouTubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    /youtu\.be\/([^&\n?#]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * YouTube URL인지 확인
 */
export function isYouTubeUrl(url: string): boolean {
  return extractYouTubeVideoId(url) !== null;
}

/**
 * YouTube 썸네일 URL 생성
 */
export function getYouTubeThumbnail(url: string, quality: 'default' | 'medium' | 'high' | 'standard' | 'maxres' = 'medium'): string | null {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) {return null;}

  const qualityMap = {
    default: 'default',      // 120x90
    medium: 'mqdefault',     // 320x180
    high: 'hqdefault',       // 480x360
    standard: 'sddefault',   // 640x480
    maxres: 'maxresdefault'  // 1280x720
  };

  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
}

/**
 * YouTube 비디오 정보 추출
 */
export function getYouTubeVideoInfo(url: string) {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) {return null;}

  return {
    videoId,
    thumbnails: {
      default: getYouTubeThumbnail(url, 'default'),
      medium: getYouTubeThumbnail(url, 'medium'),
      high: getYouTubeThumbnail(url, 'high'),
      standard: getYouTubeThumbnail(url, 'standard'),
      maxres: getYouTubeThumbnail(url, 'maxres')
    },
    embedUrl: `https://www.youtube.com/embed/${videoId}`,
    watchUrl: `https://www.youtube.com/watch?v=${videoId}`
  };
}

/**
 * 다양한 동영상 플랫폼 감지
 */
export function getVideoThumbnail(url: string): { platform: string; thumbnail: string } | null {
  // YouTube
  if (isYouTubeUrl(url)) {
    const thumbnail = getYouTubeThumbnail(url, 'medium');
    if (thumbnail) {
      return { platform: 'youtube', thumbnail };
    }
  }

  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    // Vimeo의 경우 API를 통해 썸네일을 가져와야 하므로 플레이스홀더 반환
    return { 
      platform: 'vimeo', 
      thumbnail: `https://vumbnail.com/${vimeoMatch[1]}.jpg` 
    };
  }

  return null;
}

/**
 * URL이 동영상 링크인지 확인
 */
export function isVideoUrl(url: string): boolean {
  return isYouTubeUrl(url) || 
         url.includes('vimeo.com') ||
         url.includes('dailymotion.com') ||
         url.includes('twitch.tv');
}
